import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, type ActionResult } from '../stores/platformAuthStore';
import { formStyles } from '../components/formStyles';
import { theme } from '../theme';

const styles = {
  container: { maxWidth: '560px', margin: '0 auto', padding: '32px 20px' },
  title: { fontSize: '24px', fontWeight: 700, color: theme.textPrimary, marginBottom: '24px' },
  section: {
    background: theme.bgPanel,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radiusLg,
    padding: '20px 24px',
    marginBottom: '20px',
  },
  heading: { fontSize: '16px', fontWeight: 600, color: theme.textPrimary, marginBottom: '12px' },
  text: { fontSize: '14px', color: theme.textSecondary, lineHeight: 1.5, marginBottom: '12px' },
  btn: { ...formStyles.btn, width: 'auto', padding: '8px 20px', fontSize: '14px' },
  dangerBtn: {
    ...formStyles.btn,
    width: 'auto',
    padding: '8px 20px',
    fontSize: '14px',
    background: theme.danger,
  },
  error: { ...formStyles.error, textAlign: 'left' as const },
  success: { ...formStyles.success, textAlign: 'left' as const },
};

function Status({ result }: { result: ActionResult | null }) {
  if (!result) return null;
  return result.ok ? null : <div style={styles.error}>{result.error}</div>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={styles.section}>
      <h2 style={styles.heading}>{title}</h2>
      {children}
    </div>
  );
}

function Field(props: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  minLength?: number;
}) {
  return (
    <div style={formStyles.field}>
      <label style={formStyles.label}>{props.label}</label>
      <input
        style={formStyles.input}
        type={props.type}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        required
        autoComplete={props.autoComplete}
        minLength={props.minLength}
      />
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [result, setResult] = useState<ActionResult | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const r = await useAuthStore.getState().changePassword(current, next);
    setBusy(false);
    setResult(r);
    if (r.ok) {
      setCurrent('');
      setNext('');
    }
  };

  return (
    <Section title="Change password">
      <form onSubmit={submit}>
        {result?.ok && (
          <div style={styles.success}>Password changed. Every other session was signed out.</div>
        )}
        <Status result={result} />
        <Field
          label="Current password"
          type="password"
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
        />
        <Field
          label="New password (at least 8 characters)"
          type="password"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          minLength={8}
        />
        <button style={styles.btn} type="submit" disabled={busy}>
          {busy ? 'Saving...' : 'Change password'}
        </button>
      </form>
    </Section>
  );
}

function ChangeEmail({ email }: { email: string }) {
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [result, setResult] = useState<ActionResult | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const r = await useAuthStore.getState().changeEmail(password, newEmail);
    setBusy(false);
    setResult(r);
    if (r.ok) {
      setSentTo(newEmail);
      setPassword('');
      setNewEmail('');
    }
  };

  return (
    <Section title="Change email">
      <p style={styles.text}>
        Your email is <strong>{email}</strong>. A new address takes over only once you open the link
        we send to it.
      </p>
      <form onSubmit={submit}>
        {result?.ok && <div style={styles.success}>We sent a confirmation link to {sentTo}.</div>}
        <Status result={result} />
        <Field label="New email" type="email" value={newEmail} onChange={setNewEmail} />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
        <button style={styles.btn} type="submit" disabled={busy}>
          {busy ? 'Sending...' : 'Change email'}
        </button>
      </form>
    </Section>
  );
}

function DownloadData() {
  const [result, setResult] = useState<ActionResult | null>(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    setResult(await useAuthStore.getState().downloadMyData());
    setBusy(false);
  };

  return (
    <Section title="Download my data">
      <p style={styles.text}>
        A JSON file with your account, your beta terms acceptance, the games you own and the games
        you collaborate on.
      </p>
      <Status result={result} />
      <button style={styles.btn} onClick={download} disabled={busy}>
        {busy ? 'Preparing...' : 'Download my data'}
      </button>
    </Section>
  );
}

function DeleteAccount() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [result, setResult] = useState<ActionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const r = await useAuthStore.getState().deleteAccount(password);
    setBusy(false);
    setResult(r);
    if (r.ok) {
      const when = r.deleteAfter ? new Date(r.deleteAfter).toLocaleString() : 'in 14 days';
      useAuthStore.setState({
        notice: `Your account will be deleted after ${when}. Log in before then to cancel.`,
      });
      navigate('/login');
    }
  };

  return (
    <Section title="Delete account">
      <p style={styles.text}>
        Your account and every game you own are deleted for good 14 days from now. Your running
        games stop at once. Logging in during those 14 days cancels the deletion.
      </p>
      <form onSubmit={submit}>
        <Status result={result} />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
        <Field label='Type "delete" to confirm' type="text" value={confirm} onChange={setConfirm} />
        <button style={styles.dangerBtn} type="submit" disabled={busy || confirm !== 'delete'}>
          {busy ? 'Deleting...' : 'Delete my account'}
        </button>
      </form>
    </Section>
  );
}

export function Account() {
  const { user, fetchMe } = useAuthStore();

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Account</h1>
      <ChangePassword />
      <ChangeEmail email={user?.email ?? ''} />
      <DownloadData />
      <DeleteAccount />
    </div>
  );
}
